import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, apiClient, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, ArrowLeft, Mail, Lock, User, Loader2, Fingerprint, Gift, Check, Rocket, Bell, X } from "lucide-react";
import { DarkSelect } from "@/components/ui/dark-select";
import axios from "axios";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/9fe16284-c883-46c5-9976-fb4189ef86a2/images/4414b575521af6b937b0721d2c61060a2fec8b4fac41d65ab6fa293761654c61.png";

// FEATURE FLAG: Set to false to enable registration
const REGISTRATION_BLOCKED = true;

// Coming Soon Modal Component
const LaunchingSoonModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleNotifyMe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubmitting(true);
    try {
      // Store email for waitlist
      await apiClient.post("/waitlist/subscribe", { email }).catch(() => {
        console.log("Waitlist subscription:", email);
      });
      setSubmitted(true);
      toast.success("You're on the list! We'll notify you at launch.");
    } catch (error) {
      setSubmitted(true);
      toast.success("You're on the list! We'll notify you at launch.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
        // NON-DISMISSIBLE: No onClick to close modal - enforced until launch
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-gradient-to-br from-viraly-dark to-[#1a1a2e] rounded-2xl p-8 max-w-md w-full border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* NO CLOSE BUTTON - Modal is enforced until launch */}

          {/* Rocket Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-viraly-orange to-amber-500 flex items-center justify-center animate-pulse">
              <Rocket className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-white mb-2">We're Launching Soon!</h2>
            <p className="text-viraly-muted">
              Viraly is putting the finishing touches on something amazing. Be the first to know when we go live!
            </p>
          </div>

          {/* Features Preview */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3 text-sm text-white/80">
              <div className="w-6 h-6 rounded-full bg-viraly-orange/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-viraly-orange" />
              </div>
              <span>AI-powered content creation in 71 languages</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-white/80">
              <div className="w-6 h-6 rounded-full bg-viraly-orange/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-viraly-orange" />
              </div>
              <span>Post to all 7 platforms: FB, IG, TikTok, Pinterest, LinkedIn, YouTube & X</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-white/80">
              <div className="w-6 h-6 rounded-full bg-viraly-orange/20 flex items-center justify-center">
                <Check className="w-4 h-4 text-viraly-orange" />
              </div>
              <span>Smart Auto-Pilot mode for hands-free posting</span>
            </div>
          </div>

          {/* Email Form */}
          {!submitted ? (
            <form onSubmit={handleNotifyMe} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-viraly-muted"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-viraly-orange to-amber-500 hover:from-viraly-orange/90 hover:to-amber-500/90 text-white font-semibold py-3"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Bell className="w-5 h-5 mr-2" />
                    Notify Me at Launch
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-white font-medium">You're on the waitlist!</p>
              <p className="text-viraly-muted text-sm mt-1">We'll email you when Viraly launches.</p>
            </div>
          )}

          {/* Back to Login */}
          <p className="text-center text-viraly-muted text-sm mt-4">
            Already have an account?{" "}
            <button onClick={onClose} className="text-viraly-orange hover:underline">
              Sign In
            </button>
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Forgot Password Modal Component
const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      await apiClient.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("If this email exists, you'll receive a reset link shortly.");
    } catch (error) {
      // Always show success message for security
      setSent(true);
      toast.success("If this email exists, you'll receive a reset link shortly.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail("");
    setSent(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-viraly-card border border-white/10 rounded-2xl p-8 max-w-md w-full shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-viraly-muted hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {!sent ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-viraly-orange/20 flex items-center justify-center">
                  <Mail className="w-7 h-7 text-viraly-orange" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-white">Forgot Password?</h2>
                <p className="text-viraly-muted mt-2">
                  No worries! Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-viraly-light">Email Address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-base"
                    placeholder="your@email.com"
                    required
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="btn-primary w-full"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>

              <p className="text-center text-viraly-muted text-sm mt-4">
                Remember your password?{" "}
                <button onClick={handleClose} className="text-viraly-orange hover:underline">
                  Back to Login
                </button>
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-heading font-bold text-white mb-2">Check Your Email</h2>
              <p className="text-viraly-muted">
                If an account exists for <span className="text-viraly-orange">{email}</span>, you'll receive a password reset link shortly.
              </p>
              <p className="text-viraly-muted text-sm mt-3">
                Don't forget to check your spam folder!
              </p>
              <Button onClick={handleClose} className="btn-primary mt-6">
                Back to Login
              </Button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default function AuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialMode = searchParams.get("mode") === "register" ? "register" : "login";
  const oauthCode = searchParams.get("code");
  const oauthState = searchParams.get("state");
  const oauthProvider = searchParams.get("provider"); // facebook, microsoft
  const refFromUrl = searchParams.get("ref"); // Referral code from URL
  const giftCodeFromUrl = searchParams.get("gift"); // Gift code from URL
  
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // 'google' | 'facebook' | 'microsoft' | 'webauthn'
  const [showPassword, setShowPassword] = useState(false);
  const [authProviders, setAuthProviders] = useState({ google: true, facebook: false, microsoft: false, webauthn: true });
  const [webauthnSupported, setWebauthnSupported] = useState(false);
  const [showBiometricLogin, setShowBiometricLogin] = useState(false);
  const [pendingGiftCode, setPendingGiftCode] = useState(giftCodeFromUrl || "");
  const [showLaunchingSoonModal, setShowLaunchingSoonModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  
  const { login: authLogin, user } = useAuth();
  
  // Block registration and show modal if REGISTRATION_BLOCKED is true
  useEffect(() => {
    if (REGISTRATION_BLOCKED && initialMode === "register") {
      setMode("login"); // Force to login mode
      setShowLaunchingSoonModal(true);
    }
  }, [initialMode]);
  
  // Handle mode switch with registration block
  const handleModeSwitch = (newMode) => {
    if (REGISTRATION_BLOCKED && newMode === "register") {
      setShowLaunchingSoonModal(true);
      return;
    }
    setMode(newMode);
  };
  
  // Initialize referral code from URL or localStorage
  const initialReferralCode = refFromUrl || localStorage.getItem("viraly_referral") || "";
  
  // Auto-redeem gift after login if gift code present
  useEffect(() => {
    const autoRedeemGift = async () => {
      if (user && pendingGiftCode) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `${API}/payments/gift/auto-redeem/${pendingGiftCode}`,
            { headers: { Authorization: `Bearer ${token}` }}
          );
          if (response.data.status === "success") {
            toast.success(`🎁 ${response.data.message}`);
          } else if (response.data.status === "already_redeemed") {
            toast.info("This gift has already been redeemed");
          }
          setPendingGiftCode("");
          navigate("/dashboard");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to redeem gift");
          navigate("/dashboard");
        }
      }
    };
    autoRedeemGift();
  }, [user, pendingGiftCode, navigate]);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    country: "CA",
    province: "",
    postalCode: "",
    agreeToTerms: false,
    referralCode: initialReferralCode // Editable referral code field
  });
  
  // Canadian provinces for dropdown
  const canadianProvinces = [
    { code: "ON", name: "Ontario" },
    { code: "QC", name: "Quebec" },
    { code: "BC", name: "British Columbia" },
    { code: "AB", name: "Alberta" },
    { code: "MB", name: "Manitoba" },
    { code: "SK", name: "Saskatchewan" },
    { code: "NS", name: "Nova Scotia" },
    { code: "NB", name: "New Brunswick" },
    { code: "NL", name: "Newfoundland and Labrador" },
    { code: "PE", name: "Prince Edward Island" },
    { code: "NT", name: "Northwest Territories" },
    { code: "YT", name: "Yukon" },
    { code: "NU", name: "Nunavut" }
  ];
  
  // Global countries list - All 195 UN-recognized countries
  const countries = [
    // North America
    { code: "CA", name: "Canada", hasProvinces: true },
    { code: "US", name: "United States", hasStates: true },
    { code: "MX", name: "Mexico" },
    // Europe - Western
    { code: "GB", name: "United Kingdom" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "IT", name: "Italy" },
    { code: "ES", name: "Spain" },
    { code: "NL", name: "Netherlands" },
    { code: "BE", name: "Belgium" },
    { code: "PT", name: "Portugal" },
    { code: "IE", name: "Ireland" },
    { code: "AT", name: "Austria" },
    { code: "CH", name: "Switzerland" },
    { code: "LU", name: "Luxembourg" },
    { code: "MC", name: "Monaco" },
    { code: "AD", name: "Andorra" },
    { code: "MT", name: "Malta" },
    { code: "LI", name: "Liechtenstein" },
    { code: "SM", name: "San Marino" },
    // Europe - Northern
    { code: "SE", name: "Sweden" },
    { code: "NO", name: "Norway" },
    { code: "DK", name: "Denmark" },
    { code: "FI", name: "Finland" },
    { code: "IS", name: "Iceland" },
    // Europe - Eastern
    { code: "PL", name: "Poland" },
    { code: "CZ", name: "Czech Republic" },
    { code: "SK", name: "Slovakia" },
    { code: "HU", name: "Hungary" },
    { code: "RO", name: "Romania" },
    { code: "BG", name: "Bulgaria" },
    { code: "HR", name: "Croatia" },
    { code: "SI", name: "Slovenia" },
    { code: "RS", name: "Serbia" },
    { code: "BA", name: "Bosnia and Herzegovina" },
    { code: "ME", name: "Montenegro" },
    { code: "MK", name: "North Macedonia" },
    { code: "AL", name: "Albania" },
    { code: "XK", name: "Kosovo" },
    { code: "UA", name: "Ukraine" },
    { code: "MD", name: "Moldova" },
    { code: "BY", name: "Belarus" },
    { code: "RU", name: "Russia" },
    // Europe - Baltic
    { code: "EE", name: "Estonia" },
    { code: "LV", name: "Latvia" },
    { code: "LT", name: "Lithuania" },
    // Europe - Other
    { code: "GR", name: "Greece" },
    { code: "CY", name: "Cyprus" },
    { code: "TR", name: "Turkey" },
    // Asia - East
    { code: "JP", name: "Japan" },
    { code: "KR", name: "South Korea" },
    { code: "CN", name: "China" },
    { code: "TW", name: "Taiwan" },
    { code: "HK", name: "Hong Kong" },
    { code: "MO", name: "Macau" },
    { code: "MN", name: "Mongolia" },
    { code: "KP", name: "North Korea" },
    // Asia - Southeast
    { code: "SG", name: "Singapore" },
    { code: "MY", name: "Malaysia" },
    { code: "ID", name: "Indonesia" },
    { code: "TH", name: "Thailand" },
    { code: "VN", name: "Vietnam" },
    { code: "PH", name: "Philippines" },
    { code: "MM", name: "Myanmar" },
    { code: "KH", name: "Cambodia" },
    { code: "LA", name: "Laos" },
    { code: "BN", name: "Brunei" },
    { code: "TL", name: "Timor-Leste" },
    // Asia - South
    { code: "IN", name: "India" },
    { code: "PK", name: "Pakistan" },
    { code: "BD", name: "Bangladesh" },
    { code: "LK", name: "Sri Lanka" },
    { code: "NP", name: "Nepal" },
    { code: "BT", name: "Bhutan" },
    { code: "MV", name: "Maldives" },
    { code: "AF", name: "Afghanistan" },
    // Asia - Central
    { code: "KZ", name: "Kazakhstan" },
    { code: "UZ", name: "Uzbekistan" },
    { code: "TM", name: "Turkmenistan" },
    { code: "KG", name: "Kyrgyzstan" },
    { code: "TJ", name: "Tajikistan" },
    // Middle East
    { code: "AE", name: "United Arab Emirates" },
    { code: "SA", name: "Saudi Arabia" },
    { code: "QA", name: "Qatar" },
    { code: "KW", name: "Kuwait" },
    { code: "BH", name: "Bahrain" },
    { code: "OM", name: "Oman" },
    { code: "YE", name: "Yemen" },
    { code: "JO", name: "Jordan" },
    { code: "LB", name: "Lebanon" },
    { code: "SY", name: "Syria" },
    { code: "IQ", name: "Iraq" },
    { code: "IR", name: "Iran" },
    { code: "IL", name: "Israel" },
    { code: "PS", name: "Palestine" },
    // Africa - North
    { code: "EG", name: "Egypt" },
    { code: "MA", name: "Morocco" },
    { code: "DZ", name: "Algeria" },
    { code: "TN", name: "Tunisia" },
    { code: "LY", name: "Libya" },
    { code: "SD", name: "Sudan" },
    // Africa - West
    { code: "NG", name: "Nigeria" },
    { code: "GH", name: "Ghana" },
    { code: "CI", name: "Ivory Coast" },
    { code: "SN", name: "Senegal" },
    { code: "ML", name: "Mali" },
    { code: "BF", name: "Burkina Faso" },
    { code: "NE", name: "Niger" },
    { code: "GN", name: "Guinea" },
    { code: "BJ", name: "Benin" },
    { code: "TG", name: "Togo" },
    { code: "SL", name: "Sierra Leone" },
    { code: "LR", name: "Liberia" },
    { code: "MR", name: "Mauritania" },
    { code: "GM", name: "Gambia" },
    { code: "GW", name: "Guinea-Bissau" },
    { code: "CV", name: "Cape Verde" },
    // Africa - East
    { code: "KE", name: "Kenya" },
    { code: "ET", name: "Ethiopia" },
    { code: "TZ", name: "Tanzania" },
    { code: "UG", name: "Uganda" },
    { code: "RW", name: "Rwanda" },
    { code: "BI", name: "Burundi" },
    { code: "SS", name: "South Sudan" },
    { code: "ER", name: "Eritrea" },
    { code: "DJ", name: "Djibouti" },
    { code: "SO", name: "Somalia" },
    { code: "MW", name: "Malawi" },
    { code: "MZ", name: "Mozambique" },
    { code: "ZM", name: "Zambia" },
    { code: "ZW", name: "Zimbabwe" },
    { code: "MG", name: "Madagascar" },
    { code: "MU", name: "Mauritius" },
    { code: "SC", name: "Seychelles" },
    { code: "KM", name: "Comoros" },
    // Africa - Central
    { code: "CD", name: "DR Congo" },
    { code: "CG", name: "Congo" },
    { code: "CM", name: "Cameroon" },
    { code: "GA", name: "Gabon" },
    { code: "GQ", name: "Equatorial Guinea" },
    { code: "CF", name: "Central African Republic" },
    { code: "TD", name: "Chad" },
    { code: "AO", name: "Angola" },
    { code: "ST", name: "São Tomé and Príncipe" },
    // Africa - Southern
    { code: "ZA", name: "South Africa" },
    { code: "NA", name: "Namibia" },
    { code: "BW", name: "Botswana" },
    { code: "SZ", name: "Eswatini" },
    { code: "LS", name: "Lesotho" },
    // Oceania
    { code: "AU", name: "Australia" },
    { code: "NZ", name: "New Zealand" },
    { code: "PG", name: "Papua New Guinea" },
    { code: "FJ", name: "Fiji" },
    { code: "SB", name: "Solomon Islands" },
    { code: "VU", name: "Vanuatu" },
    { code: "WS", name: "Samoa" },
    { code: "TO", name: "Tonga" },
    { code: "KI", name: "Kiribati" },
    { code: "FM", name: "Micronesia" },
    { code: "MH", name: "Marshall Islands" },
    { code: "PW", name: "Palau" },
    { code: "NR", name: "Nauru" },
    { code: "TV", name: "Tuvalu" },
    // Caribbean
    { code: "CU", name: "Cuba" },
    { code: "DO", name: "Dominican Republic" },
    { code: "HT", name: "Haiti" },
    { code: "JM", name: "Jamaica" },
    { code: "TT", name: "Trinidad and Tobago" },
    { code: "BS", name: "Bahamas" },
    { code: "BB", name: "Barbados" },
    { code: "LC", name: "Saint Lucia" },
    { code: "VC", name: "Saint Vincent and the Grenadines" },
    { code: "GD", name: "Grenada" },
    { code: "AG", name: "Antigua and Barbuda" },
    { code: "DM", name: "Dominica" },
    { code: "KN", name: "Saint Kitts and Nevis" },
    // Central America
    { code: "GT", name: "Guatemala" },
    { code: "HN", name: "Honduras" },
    { code: "SV", name: "El Salvador" },
    { code: "NI", name: "Nicaragua" },
    { code: "CR", name: "Costa Rica" },
    { code: "PA", name: "Panama" },
    { code: "BZ", name: "Belize" },
    // South America
    { code: "BR", name: "Brazil" },
    { code: "AR", name: "Argentina" },
    { code: "CL", name: "Chile" },
    { code: "CO", name: "Colombia" },
    { code: "PE", name: "Peru" },
    { code: "VE", name: "Venezuela" },
    { code: "EC", name: "Ecuador" },
    { code: "BO", name: "Bolivia" },
    { code: "PY", name: "Paraguay" },
    { code: "UY", name: "Uruguay" },
    { code: "GY", name: "Guyana" },
    { code: "SR", name: "Suriname" },
  ];
  
  const { login, register, setUser } = useAuth();

  // Check WebAuthn support on mount
  useEffect(() => {
    const checkWebAuthn = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setWebauthnSupported(available);
        } catch {
          setWebauthnSupported(false);
        }
      }
    };
    checkWebAuthn();
    fetchAuthProviders();
  }, []);

  // Fetch available auth providers
  const fetchAuthProviders = async () => {
    try {
      const response = await axios.get(`${API}/auth/providers`);
      setAuthProviders(response.data);
    } catch (error) {
      console.error("Failed to fetch auth providers:", error);
    }
  };

  // Handle OAuth callbacks (Google via session, Facebook/Microsoft via code)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check for session_id from Emergent Auth (Google)
      const sessionId = searchParams.get("session_id");
      if (sessionId && !user) {
        handleEmergentAuthCallback(sessionId);
        return;
      }
      
      // Check for OAuth code + state (Facebook/Microsoft)
      if (oauthCode && oauthState && !user) {
        const storedProvider = localStorage.getItem("viraly_oauth_provider");
        if (storedProvider === "facebook") {
          handleFacebookCallback(oauthCode, oauthState);
        } else if (storedProvider === "microsoft") {
          handleMicrosoftCallback(oauthCode, oauthState);
        }
      }
    };
    
    handleOAuthCallback();
  }, [searchParams, user]);

  // Store referral code from URL to localStorage for OAuth flows
  useEffect(() => {
    if (refFromUrl) {
      localStorage.setItem("viraly_referral", refFromUrl);
      // Switch to register mode if coming from referral link (unless registration is blocked)
      if (!REGISTRATION_BLOCKED) {
        setMode("register");
      } else {
        setShowLaunchingSoonModal(true);
      }
    }
  }, [refFromUrl]);

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Handle Emergent Auth callback (Google)
  const handleEmergentAuthCallback = async (sessionId) => {
    setSocialLoading('google');
    try {
      const response = await axios.get(`${API}/auth/session`, {
        headers: { "X-Session-ID": sessionId }
      });
      
      const { 
        access_token, 
        user: userData, 
        is_new,
        needs_gmail_auth,
        email_provider,
        email_provider_supported
      } = response.data;
      
      localStorage.setItem("viraly_token", access_token);
      localStorage.removeItem("viraly_referral");
      setUser(userData);
      
      // Handle Gmail OAuth prompt for new users or users without email permissions
      if (needs_gmail_auth && email_provider_supported) {
        // Store flag to show Gmail permission prompt on dashboard
        localStorage.setItem("viraly_needs_gmail_auth", "true");
        localStorage.setItem("viraly_email_provider", email_provider);
        
        if (is_new) {
          toast.success("Welcome to Viraly! 🚀 One more step to enable email features.");
        } else {
          toast.success("Welcome back! Enable email sending to unlock all features.");
        }
      } else if (!email_provider_supported && email_provider) {
        // User's email is not Google-hosted
        if (email_provider === "microsoft") {
          localStorage.setItem("viraly_email_provider", "microsoft");
          toast.success(is_new ? "Welcome to Viraly! 🚀" : "Welcome back!");
          toast.info("Microsoft 365 email integration coming soon!", { duration: 5000 });
        } else {
          localStorage.setItem("viraly_email_provider", "other");
          toast.success(is_new ? "Welcome to Viraly! 🚀" : "Welcome back!");
        }
      } else {
        if (is_new) {
          toast.success("Welcome to Viraly! 🚀 Your account is ready.");
        } else {
          toast.success("Welcome back!");
        }
      }
      
      navigate("/dashboard");
    } catch (error) {
      console.error("Google auth error:", error);
      const errorMsg = error.response?.data?.detail || "Google sign-in failed. Please try again.";
      toast.error(errorMsg);
      window.history.replaceState({}, document.title, "/auth");
    } finally {
      setSocialLoading(null);
    }
  };

  // Handle Facebook OAuth callback
  const handleFacebookCallback = async (code, state) => {
    setSocialLoading('facebook');
    try {
      const redirectUri = `${window.location.origin}/auth`;
      const referredBy = localStorage.getItem("viraly_referral");
      
      const response = await axios.post(`${API}/auth/facebook/callback`, null, {
        params: { code, state, redirect_uri: redirectUri, referred_by: referredBy }
      });
      
      const { access_token, user: userData, is_new } = response.data;
      localStorage.setItem("viraly_token", access_token);
      localStorage.removeItem("viraly_referral");
      localStorage.removeItem("viraly_oauth_provider");
      setUser(userData);
      
      if (is_new) {
        toast.success("Welcome to Viraly! 🚀 Your account is ready.");
      } else {
        toast.success("Welcome back!");
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Facebook auth error:", error);
      const errorMsg = error.response?.data?.detail || "Facebook sign-in failed. Please try again.";
      toast.error(errorMsg);
      window.history.replaceState({}, document.title, "/auth");
    } finally {
      setSocialLoading(null);
      localStorage.removeItem("viraly_oauth_provider");
    }
  };

  // Handle Microsoft OAuth callback
  const handleMicrosoftCallback = async (code, state) => {
    setSocialLoading('microsoft');
    try {
      const redirectUri = `${window.location.origin}/auth`;
      const referredBy = localStorage.getItem("viraly_referral");
      
      const response = await axios.post(`${API}/auth/microsoft/callback`, null, {
        params: { code, state, redirect_uri: redirectUri, referred_by: referredBy }
      });
      
      const { access_token, user: userData, is_new } = response.data;
      localStorage.setItem("viraly_token", access_token);
      localStorage.removeItem("viraly_referral");
      localStorage.removeItem("viraly_oauth_provider");
      setUser(userData);
      
      if (is_new) {
        toast.success("Welcome to Viraly! 🚀 Your account is ready.");
      } else {
        toast.success("Welcome back!");
      }
      navigate("/dashboard");
    } catch (error) {
      console.error("Microsoft auth error:", error);
      const errorMsg = error.response?.data?.detail || "Microsoft sign-in failed. Please try again.";
      toast.error(errorMsg);
      window.history.replaceState({}, document.title, "/auth");
    } finally {
      setSocialLoading(null);
      localStorage.removeItem("viraly_oauth_provider");
    }
  };

  // Google Login via Emergent Auth
  const handleGoogleLogin = async () => {
    // Check T&C acceptance for registration mode
    if (mode === "register" && !formData.agreeToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy before continuing with Google");
      return;
    }
    
    setSocialLoading('google');
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      // Construct redirect URL dynamically using window.location.origin
      const redirectUrl = window.location.origin + '/auth';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (error) {
      console.error("Google auth URL error:", error);
      const errorMsg = error.response?.data?.detail || "Google sign-in not available";
      toast.error(errorMsg);
      setSocialLoading(null);
    }
  };

  // Facebook Login
  const handleFacebookLogin = async () => {
    setSocialLoading('facebook');
    try {
      const redirectUri = `${window.location.origin}/auth`;
      const response = await axios.get(`${API}/auth/facebook/url`, {
        params: { redirect_uri: redirectUri }
      });
      
      if (response.data.auth_url) {
        localStorage.setItem("viraly_oauth_provider", "facebook");
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      console.error("Facebook auth URL error:", error);
      const errorMsg = error.response?.data?.detail || "Facebook sign-in not available";
      toast.error(errorMsg);
      setSocialLoading(null);
    }
  };

  // Microsoft Login
  const handleMicrosoftLogin = async () => {
    setSocialLoading('microsoft');
    try {
      const redirectUri = `${window.location.origin}/auth`;
      const response = await axios.get(`${API}/auth/microsoft/url`, {
        params: { redirect_uri: redirectUri }
      });
      
      if (response.data.auth_url) {
        localStorage.setItem("viraly_oauth_provider", "microsoft");
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      console.error("Microsoft auth URL error:", error);
      const errorMsg = error.response?.data?.detail || "Microsoft sign-in not available";
      toast.error(errorMsg);
      setSocialLoading(null);
    }
  };

  // WebAuthn Biometric Login
  const handleWebAuthnLogin = async () => {
    if (!formData.email) {
      toast.error("Please enter your email first");
      return;
    }
    
    setSocialLoading('webauthn');
    try {
      // Get login options from backend
      const optionsResponse = await axios.post(`${API}/auth/webauthn/login/options`, null, {
        params: { email: formData.email }
      });
      
      const options = optionsResponse.data;
      
      // Convert base64 challenge to ArrayBuffer
      const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      
      // Convert allowCredentials
      const allowCredentials = options.allowCredentials.map(cred => ({
        type: cred.type,
        id: Uint8Array.from(atob(cred.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      }));
      
      // Call WebAuthn API
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge,
          timeout: options.timeout,
          rpId: window.location.hostname === 'localhost' ? 'localhost' : options.rpId,
          userVerification: options.userVerification,
          allowCredentials
        }
      });
      
      // Encode response for backend
      const credentialId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      const clientDataJSON = btoa(String.fromCharCode(...new Uint8Array(credential.response.clientDataJSON))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      const authenticatorData = btoa(String.fromCharCode(...new Uint8Array(credential.response.authenticatorData))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      const signature = btoa(String.fromCharCode(...new Uint8Array(credential.response.signature))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
      
      // Verify with backend
      const verifyResponse = await axios.post(`${API}/auth/webauthn/login/verify`, null, {
        params: {
          email: formData.email,
          credential_id: credentialId,
          client_data: clientDataJSON,
          authenticator_data: authenticatorData,
          signature: signature
        }
      });
      
      const { access_token, user: userData } = verifyResponse.data;
      localStorage.setItem("viraly_token", access_token);
      setUser(userData);
      toast.success("Welcome back! 🔐");
      navigate("/dashboard");
      
    } catch (error) {
      console.error("WebAuthn error:", error);
      if (error.name === 'NotAllowedError') {
        toast.error("Biometric authentication was cancelled");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Biometric login failed. Try password instead.");
      }
    } finally {
      setSocialLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "register") {
        if (!formData.name.trim()) {
          toast.error("Please enter your name");
          setLoading(false);
          return;
        }
        if (!formData.agreeToTerms) {
          toast.error("Please agree to the Terms of Service and Privacy Policy");
          setLoading(false);
          return;
        }
        if (formData.country === "CA" && !formData.province) {
          toast.error("Please select your province");
          setLoading(false);
          return;
        }
        // Postal/ZIP code required for CA, US, GB, AU
        if (["CA", "US", "GB", "AU"].includes(formData.country) && !formData.postalCode.trim()) {
          toast.error(`Please enter your ${formData.country === "US" ? "ZIP code" : "postal code"}`);
          setLoading(false);
          return;
        }
        await register(
          formData.name, 
          formData.email, 
          formData.password,
          formData.country,
          formData.province,
          formData.postalCode,
          formData.referralCode
        );
        toast.success("Welcome to Viraly! 🚀");
      } else {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
      }
      navigate("/dashboard");
    } catch (error) {
      let errorMessage = "Something went wrong";
      const errorData = error.response?.data;
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message || String(e)).join(', ');
        } else if (typeof errorData.detail === 'object') {
          errorMessage = errorData.detail.msg || JSON.stringify(errorData.detail);
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const isAnyLoading = loading || socialLoading !== null;

  return (
    <div className="min-h-screen bg-viraly-dark flex">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-viraly-muted hover:text-white transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-3 mb-8">
              <img src={LOGO_URL} alt="Viraly" className="h-12 w-12" />
              <span className="font-heading font-bold text-3xl gradient-text">Viraly</span>
            </div>
            
            <h1 className="font-heading text-3xl font-bold mb-2">
              {mode === "login" ? "Welcome back" : "Get started free"}
            </h1>
            <p className="text-viraly-muted mb-8">
              {mode === "login" 
                ? "Sign in to continue to your dashboard" 
                : "Create your account and start automating"
              }
            </p>
            
            {/* Gift Code Banner */}
            {pendingGiftCode && (
              <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-viraly-orange/20 to-pink-500/20 border border-viraly-orange/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-viraly-orange/20 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-viraly-orange" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">You have a gift waiting!</p>
                    <p className="text-xs text-viraly-muted">
                      {mode === "login" ? "Sign in" : "Create an account"} to redeem your gift credits
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === "register" && (
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-viraly-light">Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-base pl-10"
                      data-testid="name-input"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-viraly-light">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input-base pl-10"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-viraly-light">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-viraly-muted" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="input-base pl-10 pr-10"
                    required
                    minLength={6}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-viraly-muted hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Address Fields - Registration Only */}
              {mode === "register" && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="country" className="text-viraly-light">Country</Label>
                      <DarkSelect
                        value={formData.country}
                        onChange={(value) => setFormData({ ...formData, country: value, province: "" })}
                        options={countries.map(c => ({ value: c.code, label: c.name }))}
                        placeholder="Select country"
                        data-testid="country-select"
                      />
                    </div>
                    
                    {formData.country === "CA" && (
                      <div className="space-y-2">
                        <Label htmlFor="province" className="text-viraly-light">Province *</Label>
                        <DarkSelect
                          value={formData.province}
                          onChange={(value) => setFormData({ ...formData, province: value })}
                          options={canadianProvinces.map(p => ({ value: p.code, label: p.name }))}
                          placeholder="Select province"
                          required
                          data-testid="province-select"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Postal/ZIP Code - Required for CA, US, UK, AU - Optional for others */}
                  {["CA", "US", "GB", "AU"].includes(formData.country) && (
                    <div className="space-y-2">
                      <Label htmlFor="postalCode" className="text-viraly-light">
                        {formData.country === "US" ? "ZIP Code" : "Postal Code"}
                        <span className="text-viraly-error ml-1">*</span>
                      </Label>
                      <Input
                        id="postalCode"
                        type="text"
                        placeholder={
                          formData.country === "CA" ? "A1A 1A1" :
                          formData.country === "US" ? "12345" :
                          formData.country === "GB" ? "SW1A 1AA" :
                          formData.country === "AU" ? "2000" : ""
                        }
                        value={formData.postalCode}
                        onChange={(e) => setFormData({ ...formData, postalCode: e.target.value.toUpperCase() })}
                        className="input-base"
                        maxLength={formData.country === "CA" || formData.country === "GB" ? 8 : 10}
                        required
                        data-testid="postal-code-input"
                      />
                      <p className="text-xs text-viraly-muted">
                        Required for locale-based content optimization
                      </p>
                    </div>
                  )}
                </>
              )}
              
              {mode === "login" && (
                <div className="flex justify-between items-center">
                  <button 
                    type="button" 
                    onClick={() => setShowForgotPasswordModal(true)}
                    className="text-sm text-viraly-orange hover:underline"
                  >
                    Forgot password?
                  </button>
                  {webauthnSupported && formData.email && (
                    <button
                      type="button"
                      onClick={handleWebAuthnLogin}
                      disabled={isAnyLoading}
                      className="flex items-center gap-2 text-sm text-viraly-orange hover:underline disabled:opacity-50"
                      data-testid="webauthn-login-btn"
                    >
                      {socialLoading === 'webauthn' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Fingerprint className="w-4 h-4" />
                      )}
                      Use Face ID / Touch ID
                    </button>
                  )}
                </div>
              )}
              
              {/* T&C Agreement - Required before Register */}
              {mode === "register" && (
                <>
                  {/* Referral Code Input */}
                  <div className="space-y-2">
                    <Label htmlFor="referralCode" className="text-viraly-light flex items-center gap-2">
                      <Gift className="w-4 h-4" />
                      Referral Code <span className="text-viraly-muted text-xs">(Optional)</span>
                    </Label>
                    <Input
                      id="referralCode"
                      placeholder="Enter referral code for 10% discount"
                      value={formData.referralCode}
                      onChange={(e) => setFormData({ ...formData, referralCode: e.target.value.toUpperCase() })}
                      className="input-base"
                      data-testid="referral-code-input"
                    />
                    {formData.referralCode && (
                      <p className="text-xs text-green-400 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Referral code applied - you'll get 10% off your first purchase!
                      </p>
                    )}
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                    <Checkbox
                      id="terms-agree"
                      checked={formData.agreeToTerms}
                      onCheckedChange={(checked) => setFormData({ ...formData, agreeToTerms: checked })}
                      className="mt-0.5 border-viraly-muted data-[state=checked]:bg-viraly-orange data-[state=checked]:border-viraly-orange"
                      data-testid="terms-checkbox"
                    />
                    <label htmlFor="terms-agree" className="text-sm text-viraly-muted cursor-pointer leading-tight">
                      I agree to the{" "}
                      <Link to="/terms" className="text-viraly-orange hover:underline">Terms of Service</Link>
                      {" "}and{" "}
                      <Link to="/privacy" className="text-viraly-orange hover:underline">Privacy Policy</Link>
                      , including access to my connected social media accounts for personalized content generation
                      <span className="text-viraly-error ml-1">*</span>
                    </label>
                  </div>
                </>
              )}
              
              <Button 
                type="submit" 
                className="btn-primary w-full py-3"
                disabled={isAnyLoading}
                data-testid="auth-submit-btn"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  mode === "login" ? "Sign In" : "Create Account"
                )}
              </Button>
            </form>
            
            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-viraly-dark text-viraly-muted">Or continue with</span>
              </div>
            </div>
            
            {/* Social Login Buttons - Clean Grid Layout */}
            <div className="space-y-3">
              {/* Primary: Google - Full Width */}
              <button
                type="button"
                onClick={handleGoogleLogin}
                disabled={isAnyLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white text-gray-800 font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                data-testid="google-login-btn"
              >
                {socialLoading === 'google' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Continue with Google
              </button>
              
              {/* Facebook Login Button - Hidden until native API approval */}
              {/* 
              NOTE: Facebook login is disabled until we get native API approval.
              The backend has the integration ready - just uncomment this when approved.
              <button
                type="button"
                onClick={handleFacebookLogin}
                disabled={isAnyLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-[#1877F2] text-white font-medium hover:bg-[#1565D8] transition-colors disabled:opacity-50"
                data-testid="facebook-login-btn"
              >
                {socialLoading === 'facebook' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                )}
                Continue with Facebook
              </button>
              */}
              
              {/* Note about connecting other platforms */}
              <p className="text-xs text-center text-viraly-muted">
                Connect Instagram, TikTok & Pinterest after signing in
              </p>
              
              {/* Biometric Login */}
              {mode === "login" && webauthnSupported && (
                <button
                  type="button"
                  onClick={handleWebAuthnLogin}
                  disabled={isAnyLoading || !formData.email}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-viraly-gradient text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  data-testid="biometric-login-btn"
                >
                  {socialLoading === 'webauthn' ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Fingerprint className="w-5 h-5" />
                  )}
                  Sign in with Face ID / Touch ID
                  {!formData.email && <span className="text-xs opacity-70">(Enter email first)</span>}
                </button>
              )}
            </div>
            
            <div className="mt-8 text-center">
              <p className="text-viraly-muted">
                {mode === "login" ? "Don't have an account?" : "Already have an account?"}
                <button
                  type="button"
                  onClick={() => handleModeSwitch(mode === "login" ? "register" : "login")}
                  className="ml-2 text-viraly-orange hover:underline font-medium"
                  data-testid="toggle-auth-mode"
                >
                  {mode === "login" ? "Sign up" : "Sign in"}
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Launching Soon Modal */}
      <LaunchingSoonModal 
        isOpen={showLaunchingSoonModal} 
        onClose={() => setShowLaunchingSoonModal(false)} 
      />
      
      {/* Right Panel - Visual */}
      <div className="hidden lg:flex w-1/2 bg-viraly-card relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-radial from-viraly-orange/20 via-transparent to-transparent" />
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center p-12"
        >
          <div className="w-32 h-32 mx-auto mb-8 rounded-3xl bg-viraly-gradient flex items-center justify-center neon-glow animate-glow-pulse">
            <img src={LOGO_URL} alt="Viraly" className="h-20 w-20" />
          </div>
          
          <h2 className="font-heading text-4xl font-bold mb-4">
            One input.
            <br />
            <span className="gradient-text">Pay as you go or monthly.</span>
          </h2>
          <p className="text-viraly-muted text-lg max-w-sm mx-auto">
            AI-powered social media automation for content creators who mean business.
          </p>
          
          <div className="mt-12 flex items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-3xl font-heading font-bold gradient-text">5</p>
              <p className="text-viraly-muted text-sm">Free posts</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-3xl font-heading font-bold gradient-text">7</p>
              <p className="text-viraly-muted text-sm">Day trial</p>
            </div>
            <div className="w-px h-12 bg-white/10" />
            <div className="text-center">
              <p className="text-3xl font-heading font-bold gradient-text">7</p>
              <p className="text-viraly-muted text-sm">Platforms</p>
            </div>
          </div>
        </motion.div>
      </div>
      
      {/* Forgot Password Modal */}
      <ForgotPasswordModal 
        isOpen={showForgotPasswordModal} 
        onClose={() => setShowForgotPasswordModal(false)} 
      />
    </div>
  );
}
